<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  UiButton,
  UiIcon,
  UiCard,
  UiInputField,
  UiNumberField,
  UiSwitch,
  UiTag,
  UiTabs,
  UiTabsItem,
  UiText,
  UiTextarea,
  UiRichTextEditor,
  useApi,
  useToaster,
} from "@shopexperts/shared";
import type {
  AdminSetting,
  AdminSettingValue,
  UpdateAdminSettingsRequest,
} from "@shopexperts/legacy-api";
import {
  createAdminSettingDraft,
  formatAdminSettingGroup,
  isAdminSettingChanged,
  parseAdminSettingDraft,
  type AdminSettingDraftValue,
} from "@/app/utils/adminSettings";

import { inviteTemplateField } from "@/app/utils/inviteEmailTemplates";
import ReviewBackgroundPresetsField from '@/app/components/admin-settings/ReviewBackgroundPresetsField.vue';

const ADMIN_SETTINGS_QUERY_KEY = ["admin", "settings"] as const;

const api = useApi();
const queryClient = useQueryClient();
const toaster = useToaster();
const search = ref("");
const isUploadingPreset = ref(false);
const activeTab = ref("all");
const savedSettings = ref<AdminSetting[]>([]);
const drafts = ref<Record<string, AdminSettingDraftValue>>({});

const {
  data: queriedSettings,
  error: settingsError,
  isError: isSettingsError,
  isLoading: isSettingsLoading,
  refetch: refetchSettings,
} = useQuery({
  queryKey: ADMIN_SETTINGS_QUERY_KEY,
  queryFn: () => api.admin.settings.list(),
  staleTime: 60_000,
});

const {
  isPending: isSaving,
  mutateAsync: updateSettings,
} = useMutation({
  mutationFn: (body: UpdateAdminSettingsRequest) => api.admin.settings.update({ body }),
});

function hydrateSettings(settings: AdminSetting[]) {
  savedSettings.value = settings;
  drafts.value = Object.fromEntries(
    settings.map((setting) => [setting.key, createAdminSettingDraft(setting)]),
  );
}

watch(
  queriedSettings,
  (settings) => {
    if (!settings) return;
    if (savedSettings.value.length > 0 && savedSettings.value.some(isRowDirty)) return;
    hydrateSettings(settings);
  },
  { immediate: true },
);

watch(settingsError, (error) => {
  if (!error) return;
  toaster.create({ title: "Failed to load admin settings", type: "error" });
});

const filteredSettings = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return savedSettings.value;

  return savedSettings.value.filter((setting) =>
    [setting.key, setting.title, setting.description, setting.group, setting.type]
      .some((value) => value.toLowerCase().includes(query)),
  );
});

const settingTabs = computed(() => {
  const groups = new Map<string, AdminSetting[]>();
  for (const setting of savedSettings.value) {
    const group = groups.get(setting.group) ?? [];
    group.push(setting);
    groups.set(setting.group, group);
  }

  return [
    { value: "all", label: "All", settings: filteredSettings.value },
    ...Array.from(groups, ([group, settings]) => ({
      value: `group:${group}`,
      label: formatAdminSettingGroup(group),
      settings,
    })),
  ];
});

watch(search, (value) => {
  if (value.trim()) activeTab.value = "all";
});

watch(activeTab, (value) => {
  if (value !== "all") search.value = "";
});

function draftFor(setting: AdminSetting): AdminSettingDraftValue {
  return drafts.value[setting.key] ?? createAdminSettingDraft(setting);
}

function stringDraftFor(setting: AdminSetting): string {
  const draft = draftFor(setting);
  return typeof draft === "string" ? draft : "";
}

function booleanDraftFor(setting: AdminSetting): boolean {
  return draftFor(setting) === true;
}

function updateStringDraft(setting: AdminSetting, value: string | undefined) {
  drafts.value[setting.key] = value ?? "";
}

function updateBooleanDraft(setting: AdminSetting, value: boolean) {
  drafts.value[setting.key] = value;
}

function isRowDirty(setting: AdminSetting): boolean {
  const draft = draftFor(setting);
  const parsed = parseAdminSettingDraft(setting, draft);
  if (parsed.error) return draft !== createAdminSettingDraft(setting);
  return isAdminSettingChanged(setting, draft);
}

function fieldError(setting: AdminSetting): string | undefined {
  if (!isRowDirty(setting)) return undefined;
  return parseAdminSettingDraft(setting, draftFor(setting)).error ?? undefined;
}

const dirtySettings = computed(() => savedSettings.value.filter(isRowDirty));
const dirtyCount = computed(() => dirtySettings.value.length);
const invalidCount = computed(() => dirtySettings.value.filter((setting) => fieldError(setting)).length);
const uniqueGroupCount = computed(() => new Set(savedSettings.value.map((setting) => setting.group)).size);

function resetChanges() {
  hydrateSettings(savedSettings.value);
}

async function refreshSettings() {
  const result = await refetchSettings();
  if (result.data) hydrateSettings(result.data);
}

function mergeUpdatedSettings(updated: AdminSetting[]): AdminSetting[] {
  const updatedByKey = new Map(updated.map((setting) => [setting.key, setting]));
  return savedSettings.value.map((setting) => updatedByKey.get(setting.key) ?? setting);
}

async function saveChanges() {
  if (dirtyCount.value === 0 || isSaving.value || isUploadingPreset.value) return;

  const settings: Array<{ key: string; value: AdminSettingValue }> = [];
  for (const setting of dirtySettings.value) {
    const parsed = parseAdminSettingDraft(setting, draftFor(setting));
    if (parsed.error !== null) continue;
    settings.push({ key: setting.key, value: parsed.value });
  }

  if (settings.length !== dirtyCount.value) {
    toaster.create({
      title: "Fix the highlighted values before saving",
      type: "error",
    });
    return;
  }

  try {
    const changeCount = settings.length;
    const updated = await updateSettings({ settings });
    hydrateSettings(mergeUpdatedSettings(updated));
    void queryClient.invalidateQueries({ queryKey: ADMIN_SETTINGS_QUERY_KEY });
    toaster.create({
      title: `${changeCount} ${changeCount === 1 ? "setting" : "settings"} saved`,
      type: "success",
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error";
    toaster.create({ title: `Failed to save settings: ${message}`, type: "error" });
  }
}

function isExpandedField(setting: AdminSetting): boolean {
  return isEmailBody(setting)
    || ["text", "array", "object"].includes(setting.type);
}

function isEmailBody(setting: AdminSetting): boolean {
  return inviteTemplateField(setting.key) === "body"
    || setting.key === "admin_emails.project_review_initial_body"
    || setting.key === "admin_emails.project_review_reminder_body";
}

</script>

<template>
  <main class="min-w-0 flex-1 overflow-y-auto bg-secondary px-4 py-6 md:px-8 md:py-8">
    <div class="mx-auto flex w-full max-w-[1120px] flex-col gap-6">
      <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex flex-col gap-2">
          <UiText as="h1" variant="h3" class="font-semibold" text="Settings" />
          <UiText as="p" variant="p-small" class="text-se-gray-600" text="Manage platform preferences and email templates." />
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <UiButton
            variant="ghost"
            label="Discard changes"
            :disabled="dirtyCount === 0 || isSaving || isUploadingPreset"
            @click="resetChanges"
          />
          <UiButton
            variant="primary"
            :label="isSaving
              ? 'Saving…'
              : dirtyCount > 0
                ? `Save ${dirtyCount} ${dirtyCount === 1 ? 'change' : 'changes'}`
                : 'All changes saved'"
            :disabled="dirtyCount === 0 || invalidCount > 0 || isSaving || isUploadingPreset"
            @click="saveChanges"
          >
            <template #before_label>
              <span
                v-if="isSaving"
                class="h-4 w-4 animate-spin rounded-full border-2 border-se-gray-300 border-t-se-white"
                aria-hidden="true"
              />
              <UiIcon v-else name="check" class="h-4 w-4" />
            </template>
          </UiButton>
        </div>
      </header>

      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="w-full sm:max-w-md">
          <UiInputField
            v-model="search"
            type="search"
            icon="search-md"
            clearable
            aria-label="Search all settings"
            placeholder="Search all settings…"
          />
        </div>
        <UiText as="span" variant="p-small" class="text-se-gray-500" :text="`${savedSettings.length} settings across ${uniqueGroupCount} groups`" />
      </div>

      <section class="min-w-0">
        <div v-if="isSettingsLoading" class="flex min-h-72 flex-col items-center justify-center gap-3 p-10">
          <span class="h-8 w-8 animate-spin rounded-full border-2 border-se-gray-200 border-t-se-dark" aria-hidden="true" />
          <UiText as="p" variant="p-small" class="text-se-gray-600" text="Loading settings…" />
        </div>

        <div v-else-if="isSettingsError" class="flex min-h-72 flex-col items-center justify-center gap-3 p-10 text-center">
          <div class="flex h-11 w-11 items-center justify-center rounded-full bg-red-50">
            <UiIcon name="info-circle" class="h-5 w-5 text-red-600" />
          </div>
          <UiText as="h2" variant="h5" class="font-semibold" text="Settings could not be loaded" />
          <UiText as="p" variant="p-small" class="text-se-gray-600" text="Check the API connection and try again." />
          <UiButton variant="outline" label="Try again" @click="refreshSettings" />
        </div>

        <UiTabs v-else v-model="activeTab" orientation="vertical" class="min-w-0">
          <UiTabsItem
            v-for="tab in settingTabs"
            :key="tab.value"
            :value="tab.value"
            :label="tab.label"
            :count="tab.settings.length"
          >
            <div v-if="tab.settings.length === 0" class="flex min-h-64 flex-col items-center justify-center gap-3 p-10 text-center">
              <div class="flex h-11 w-11 items-center justify-center rounded-full bg-se-gray-100">
                <UiIcon name="search-md" class="h-5 w-5 text-se-gray-600" />
              </div>
              <UiText as="h2" variant="h5" class="font-semibold" text="No settings found" />
              <UiText as="p" variant="p-small" class="text-se-gray-600" text="Try a different key, title, or group." />
              <UiButton variant="ghost" label="Clear search" @click="search = ''" />
            </div>

            <div v-else class="flex flex-col gap-4">
              <UiCard
                v-for="setting in tab.settings"
                :key="setting.key"
                padding="md"
                :class="{ 'border-amber-400': isRowDirty(setting) }"
              >
                <div
                  class="grid min-w-0 gap-5"
                  :class="isExpandedField(setting) ? 'grid-cols-1' : 'md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:items-start'"
                >
                  <div class="flex min-w-0 flex-col gap-2">
                    <div class="flex flex-wrap items-center gap-2">
                      <UiText as="h2" variant="p" class="font-semibold" :text="setting.title" />
                      <UiTag v-if="isRowDirty(setting)" text="Unsaved" variant="secondary" />
                      <UiTag v-if="fieldError(setting)" text="Needs attention" variant="secondary" />
                    </div>
                    <UiText as="p" variant="p-small" class="max-w-2xl text-se-gray-600" :text="setting.description" />
                    <UiText v-if="activeTab === 'all'" as="span" variant="caption-normal" class="text-se-gray-500" :text="formatAdminSettingGroup(setting.group)" />
                  </div>
                  <div class="min-w-0">
                <ReviewBackgroundPresetsField v-if="setting.key === 'expert_profiles.review_share_background_presets'"
                  :model-value="stringDraftFor(setting)" :disabled="isSaving || isUploadingPreset"
                  @update:model-value="updateStringDraft(setting, $event)" @busy="isUploadingPreset = $event" />
                <div v-else-if="setting.type === 'boolean'" class="flex min-h-11 items-center gap-3">
                  <UiSwitch
                    :model-value="booleanDraftFor(setting)"
                    :disabled="isSaving"
                    :aria-label="`Change ${setting.title}`"
                    @update:model-value="updateBooleanDraft(setting, $event)"
                  />
                  <UiText
                    as="span"
                    variant="p-small"
                    class="font-semibold"
                    :class="booleanDraftFor(setting) ? 'text-emerald-700' : 'text-se-gray-500'"
                    :text="booleanDraftFor(setting) ? 'On' : 'Off'"
                  />
                </div>

                <UiNumberField
                  v-else-if="setting.type === 'integer' || setting.type === 'float'"
                  :model-value="stringDraftFor(setting)"
                  :name="setting.key"
                  :aria-label="setting.title"
                  :step="setting.type === 'integer' ? 1 : 0.01"
                  :disabled="isSaving"
                  :invalid="Boolean(fieldError(setting))"
                  :error="fieldError(setting)"
                  @update:model-value="updateStringDraft(setting, $event)"
                />

                <div v-else-if="isEmailBody(setting)" class="min-w-0">
                  <UiRichTextEditor
                    :model-value="stringDraftFor(setting)"
                    :aria-label="setting.title"
                    :rows="10"
                    :disabled="isSaving"
                    @update:model-value="updateStringDraft(setting, $event)"
                  />
                  <UiText v-if="fieldError(setting)" as="p" variant="caption-normal" class="text-red-600" :text="fieldError(setting)" />
                </div>

                <UiTextarea
                  v-else-if="setting.type === 'text' || setting.type === 'array' || setting.type === 'object'"
                  :model-value="stringDraftFor(setting)"
                  :name="setting.key"
                  :aria-label="setting.title"
                  :rows="setting.type === 'text' ? 6 : 4"
                  :disabled="isSaving"
                  :invalid="Boolean(fieldError(setting))"
                  :error="fieldError(setting)"
                  @update:model-value="updateStringDraft(setting, $event)"
                />

                <UiInputField
                  v-else
                  :model-value="stringDraftFor(setting)"
                  :name="setting.key"
                  :aria-label="setting.title"
                  :disabled="isSaving"
                  :invalid="Boolean(fieldError(setting))"
                  :error="fieldError(setting)"
                  @update:model-value="updateStringDraft(setting, $event)"
                />
                  </div>
                </div>
              </UiCard>
            </div>
          </UiTabsItem>
        </UiTabs>
      </section>

      <div v-if="dirtyCount > 0" class="sticky bottom-4 z-10 flex flex-col gap-3 rounded-lg border border-se-dark bg-se-dark px-4 py-3 text-white shadow-xl sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-3">
          <span class="h-2.5 w-2.5 rounded-full bg-se-primary" aria-hidden="true" />
          <UiText
            as="p"
            variant="p-small"
            :text="invalidCount > 0
              ? `${invalidCount} ${invalidCount === 1 ? 'value needs' : 'values need'} attention before saving.`
              : `${dirtyCount} unsaved ${dirtyCount === 1 ? 'change' : 'changes'} ready to save.`"
          />
        </div>
        <UiButton
          variant="primary"
          theme="dark"
          :label="isSaving ? 'Saving…' : 'Save changes'"
          :disabled="invalidCount > 0 || isSaving || isUploadingPreset"
          @click="saveChanges"
        />
      </div>
    </div>
  </main>
</template>
